import { CSSProperties, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { IPartnerContainerProps, IPartnerEngagementsDetails } from "../../interfaces/PartnerView.interfaces";
import { useLazyGetPartnerEngagementDataQuery } from "../../slices/ExplorePartners.slice";
import PartnerPartnerEngagementChart from "./PartnerEngagementChart";
import PartnerEngagementTable from "./PartnerEngagementTable";
import WarningBanner from "../../helper/WarningBanner";

export default function PartnerEngagementContainer(props: Readonly<IPartnerContainerProps>) {
  // Properties
  const [filteredItems, setFilteredItems] = useState<IPartnerEngagementsDetails[]>([]);
  const [searchParams] = useSearchParams();
  // Change: chartFilters as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const partnerOneID = searchParams.get("partnerOneId");
  const mpnID = searchParams.get("mpnId");

  const [getPartnerEngagementsData, partnerData] = useLazyGetPartnerEngagementDataQuery();

  useEffect(() => {
    if (partnerOneID && mpnID) {
      getPartnerEngagementsData({
        partnerOneID: partnerOneID,
        mpnIdentifier: mpnID
      });
    }
  }, [partnerOneID, mpnID, getPartnerEngagementsData]);

  // Events

  const showNoData = () => {
    const rootStyle: CSSProperties = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{"Partner Engagement Details not available."}</div>;
  };

  const customProps = {
    partnerOneID: partnerOneID,
    partnerOneName: props?.partnerOneName
  };

  // Hooks
  useEffect(() => {
    logEvent("Tenant  Details", "Partner Page", "PartnerEngagementContainer", customProps);
  }, [customProps]);

  useEffect(() => {
    setFilteredItems(partnerData?.data?.partnerEngagementDetails ?? []);
    if (partnerData.error) {
      logException(
        "Partner View",
        "PartnerEngagementContainer",
        "useGetPartnerEngagementDataQuery",
        partnerData.error,
        customProps
      );
    }
  }, [partnerData?.data, partnerData.error, partnerData]);

  useEffect(() => {
    const filteredData = (partnerData.data?.partnerEngagementDetails || []).filter(
      ({ programType, programState, engagementOwner, meetingType, programName }: any) => {
        // If no filters, show all data
        if (chartFilters.length === 0) {
          return true;
        }

        // Apply all filters - item must match ALL active filters
        return chartFilters.every((filter) => {
          if (filter.filterKey === "programType") {
            return programType === filter.value;
          } else if (filter.filterKey === "programState") {
            return programState === filter.value;
          } else if (filter.filterKey === "engagementOwner") {
            return engagementOwner === filter.value;
          } else if (filter.filterKey === "meetingType") {
            return meetingType === filter.value;
          } else if (filter.filterKey === "programName") {
            return programName === filter.value;
          }
          return true;
        });
      }
    );
    setFilteredItems(filteredData);
  }, [chartFilters, partnerData?.data]);

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  if (!partnerData.isLoading && partnerData?.error) {
    return <DisplayAPIsException error={partnerData.error} height={250} />;
  }

  return (
    <>
      {!partnerData.isLoading && filteredItems?.length > 0 && (
        <div style={{ marginLeft: 10, marginRight: 10, marginBottom: 10 }}>
          <WarningBanner message="This page displays CxE Engagements which were active in the last 12 months" />
        </div>
      )}
      <PartnerPartnerEngagementChart
        loading={partnerData.isLoading}
        onSelectedChartFilter={handleChartFilter}
        options={{
          filteredData: filteredItems,
          originalData: partnerData.data?.partnerEngagementDetails || [],
          chartOptions: [
            {
              id: 1,
              field: "engagementOwner",
              key: "partner-view-programtype-details-chart-by-engagementOwner",
              title: "Engagement Owner",
              tooltipDescription: "Distribution of engagements by their owners.",
              type: "donut",
              kustoLink: partnerData?.data?.engagementOwnerKustoLink
            },
            {
              id: 2,
              field: "meetingType",
              key: "partner-view-partner-profile-chart-by-meetingType",
              title: "Meeting Type",
              tooltipDescription: "Distribution of engagements by their meeting types.",
              type: "donut",
              kustoLink: partnerData?.data?.meetingTypeKustoLink
            },
            {
              id: 3,
              field: "programName",
              key: "partner-view-partner-profile-chart-by-programName",
              title: "Program Name",
              tooltipDescription: "Distribution of engagements by their program names.",
              type: "donut",
              kustoLink: partnerData?.data?.programNameKustoLink
            }
          ]
        }}
      />
      {partnerData.isLoading ? (
        <CustomShimmer columnCount={11} />
      ) : (
        (partnerData.isSuccess &&
          Array.isArray(partnerData?.data?.partnerEngagementDetails) &&
          partnerData?.data?.partnerEngagementDetails.length > 0 && (
            <div style={{ marginLeft: 8, marginRight: 8 }}>
              <PartnerEngagementTable
                tableData={filteredItems}
                partnerOneName={props.partnerOneName}
                kustoLink={partnerData?.data?.partnerEngagementDetailsKustoLink}
              />
            </div>
          )) ||
        showNoData()
      )}
    </>
  );
}
